import axios from 'axios'
import firebase from 'firebase/app'
import 'firebase/auth'

const DEFAULT_LOGIN_PIN_REQUEST_URL = 'https://us-central1-people2-admin.cloudfunctions.net/requestLoginPin'
const DEFAULT_LOGIN_PIN_VERIFY_URL = 'https://us-central1-people2-admin.cloudfunctions.net/verifyLoginPin'
const DEFAULT_SWITCH_LOGIN_ACCOUNT_URL = 'https://us-central1-people2-admin.cloudfunctions.net/switchLoginAccount'
const ALLOWED_CONSOLE_USER_TYPES = new Set([
  'admin',
  'hr',
  'superadmin',
  'ghost',
  'leadership',
  'superleader',
  'advisor',
  'adviser',
  'manager',
])

export const isAllowedConsoleUserType = (userType: any, isAdviser = false): boolean => {
  if (isAdviser) return true
  return ALLOWED_CONSOLE_USER_TYPES.has(String(userType || '').toLowerCase())
}

export type LoginPinRequestResult = {
  ok: boolean
  message?: string
  email?: string
  is_adviser?: boolean
}

export type LoginPinVerificationResult = {
  verified: boolean
  message?: string
  locked?: boolean
  retry_after_seconds?: number
  locked_until?: string
  remaining_attempts?: number
  customToken?: string
  email?: string
  emailKey?: string
  company?: string
  user_type?: string
  is_adviser?: boolean
}

export type SwitchLoginAccountResult = {
  ok: boolean
  requiresPin?: boolean
  message?: string
  customToken?: string
  email?: string
  emailKey?: string
  company?: string
  user_type?: string
  is_adviser?: boolean
}

export const requestLoginPin = async (
  email: string,
  options: { isAdviser?: boolean } = {}
): Promise<LoginPinRequestResult> => {
  const normalizedEmail = String(email || '').toLowerCase().trim()

  if (!normalizedEmail) {
    return {
      ok: false,
      message: 'Please recheck your login credentials.',
    }
  }

  const requestUrl = process.env.VUE_APP_LOGIN_PIN_REQUEST_URL || DEFAULT_LOGIN_PIN_REQUEST_URL

  try {
    const response = await axios.post(
      requestUrl,
      {
        email: normalizedEmail,
        isAdviser: !!options.isAdviser,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )
    const data = response.data || {}

    if (data.ok === true) {
      return {
        ok: true,
        email: data.email,
        is_adviser: data.is_adviser,
      }
    }

    return {
      ok: false,
      message: data.message || 'Please recheck your login credentials.',
    }
  } catch (error) {
    const err: any = error
    const status = err && err.response ? err.response.status : null

    if (status && status >= 400 && status < 500) {
      return {
        ok: false,
        message: (err.response.data && err.response.data.message) || 'Please recheck your login credentials.',
      }
    }

    return {
      ok: false,
      message: 'Verification is temporarily unavailable. Please try again in a few minutes.',
    }
  }
}

export const verifyLoginPin = async (
  email: string,
  pincode: string,
  options: { isAdviser?: boolean } = {}
): Promise<LoginPinVerificationResult> => {
  const normalizedEmail = String(email || '').toLowerCase().trim()
  const normalizedPin = String(pincode || '').trim()

  if (!normalizedEmail || !normalizedPin) {
    return {
      verified: false,
      message: 'Please recheck your PIN code',
    }
  }

  const verifyUrl = process.env.VUE_APP_LOGIN_PIN_VERIFY_URL || DEFAULT_LOGIN_PIN_VERIFY_URL

  try {
    const response = await axios.post(
      verifyUrl,
      {
        email: normalizedEmail,
        pincode: normalizedPin,
        isAdviser: !!options.isAdviser,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )
    const data = response.data || {}

    if (data.verified === true && data.customToken) {
      return {
        verified: true,
        customToken: data.customToken,
        email: data.email,
        emailKey: data.emailKey,
        company: data.company,
        user_type: data.user_type,
        is_adviser: data.is_adviser,
      }
    }

    return {
      verified: false,
      message: data.message || 'Please recheck your PIN code',
      locked: data.locked === true,
      retry_after_seconds: data.retry_after_seconds,
      locked_until: data.locked_until,
      remaining_attempts: data.remaining_attempts,
    }
  } catch (error) {
    const err: any = error
    const status = err && err.response ? err.response.status : null
    const data = err && err.response ? (err.response.data || {}) : {}

    if (status && status >= 400 && status < 500) {
      return {
        verified: false,
        message: data.message || 'Please recheck your PIN code',
        locked: data.locked === true,
        retry_after_seconds: data.retry_after_seconds,
        locked_until: data.locked_until,
        remaining_attempts: data.remaining_attempts,
      }
    }

    return {
      verified: false,
      message: 'Verification is temporarily unavailable. Please try again in a few minutes.',
    }
  }
}

export const switchLoginAccount = async (targetEmail: string): Promise<SwitchLoginAccountResult> => {
  const normalizedEmail = String(targetEmail || '').toLowerCase().trim()
  const currentUser = firebase.auth().currentUser

  if (!normalizedEmail || !currentUser) {
    return {
      ok: false,
      requiresPin: true,
      message: 'Please verify this advisor account again.',
    }
  }

  const switchUrl = process.env.VUE_APP_SWITCH_LOGIN_ACCOUNT_URL || DEFAULT_SWITCH_LOGIN_ACCOUNT_URL

  try {
    const idToken = await currentUser.getIdToken()
    const response = await axios.post(
      switchUrl,
      { targetEmail: normalizedEmail },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
      }
    )
    const data = response.data || {}

    if (data.ok === true && data.customToken) {
      return {
        ok: true,
        customToken: data.customToken,
        email: data.email,
        emailKey: data.emailKey,
        company: data.company,
        user_type: data.user_type,
        is_adviser: data.is_adviser,
      }
    }

    return {
      ok: false,
      requiresPin: data.error === 'switch_grant_required',
      message: data.message || 'Please verify this advisor account again.',
    }
  } catch (error) {
    const err: any = error
    const data = err && err.response ? (err.response.data || {}) : {}

    if (data.error === 'switch_grant_required') {
      return {
        ok: false,
        requiresPin: true,
        message: data.message || 'Please verify this advisor account again.',
      }
    }

    return {
      ok: false,
      message: data.message || 'Unable to switch account. Please try again.',
    }
  }
}
